import React, { Fragment } from 'react';

import CopyTextInput from '@dashboard/components/form/fields/CopyTextInput';
import LoadingIndicator from '@dashboard/components/loading/LoadingIndicator';
import BaseSettingCard from '@dashboard/components/platform-settings/BaseSettingCard';
import { PlatformUserStatus } from '@dashboard/generated/wp/authentication';
import { Chip, Divider, Stack, Typography } from '@mui/material';

import { DateFormatter } from '@waypoint/react/formatters';

import InviteEmailAddressForm from './InviteEmailAddressForm';
import ActionsMenu from './MemberRow/ActionsMenu';
import PlatformUserRoleMenu from './PlatformUserRoleMenu';
import useMembersQuery from './useMembersQuery';

function renderFormattedDate(date: string | null) {
  if (!date) {
    return null;
  }
  return <DateFormatter value={date} />;
}

type StatusLineProps = {
  status: PlatformUserStatus;
  inviteAcceptedAt: string | null;
  inviteLastSentAt: string | null;
};
function StatusLine({ status, inviteAcceptedAt, inviteLastSentAt }: StatusLineProps) {
  switch (status) {
    case PlatformUserStatus.Accepted:
      return (
        <Typography variant="body2" color="text.secondary">
          Joined {renderFormattedDate(inviteAcceptedAt)}
        </Typography>
      );
    case PlatformUserStatus.Invited:
      return (
        <Typography variant="body2" color="text.secondary">
          Invited {renderFormattedDate(inviteLastSentAt)}
        </Typography>
      );
    case PlatformUserStatus.Removed:
      return <Chip size="small" variant="outlined" color="error" label={status} />;
  }
}

type InviteLink = {
  status: PlatformUserStatus;
  inviteUrl: string | null | undefined;
};

function InviteLink({ status, inviteUrl }: InviteLink) {
  if (status !== PlatformUserStatus.Invited || !inviteUrl) {
    return null;
  }
  return <CopyTextInput label="Invite URL" value={inviteUrl} margin="normal" />;
}

function MembersSection(): JsxNode {
  const { data } = useMembersQuery();
  const nodes = data?.Platform.platformUsers.nodes;

  if (nodes === undefined) {
    return <LoadingIndicator />;
  }
  return (
    <Stack spacing={3} pt={1} sx={{ '.MuiDivider-root:last-child': { display: 'none' } }}>
      {nodes.map((platformUser) => (
        <Fragment key={platformUser.id}>
          <Stack spacing={3} sx={{ width: '100%' }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
            >
              <Stack spacing={0.25}>
                <Typography noWrap>{platformUser.emailAddress || platformUser.displayName}</Typography>
                <StatusLine
                  status={platformUser.status}
                  inviteAcceptedAt={platformUser.inviteAcceptedAt}
                  inviteLastSentAt={platformUser.inviteLastSentAt}
                />
              </Stack>

              <Stack direction="row" spacing={0.5} justifyContent="space-between" alignItems="center">
                <PlatformUserRoleMenu platformUser={platformUser} />
                <ActionsMenu platformUser={platformUser} />
              </Stack>
            </Stack>
            <InviteLink status={platformUser.status} inviteUrl={platformUser.inviteUrl} />
          </Stack>
          <Divider />
        </Fragment>
      ))}
    </Stack>
  );
}

export default function TeamPage() {
  return (
    <BaseSettingCard
      title="Team members"
      helpUrl="https://www.usewaypoint.com/docs/manage-team"
      actions={<InviteEmailAddressForm />}
      headerSx={{
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 1, sm: 0 },
        alignItems: { xs: 'start', sm: 'center' },
      }}
    >
      <MembersSection />
    </BaseSettingCard>
  );
}
